import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

/**
 * Hook for syncing Redux state with sessionStorage
 *
 * @param key - SessionStorage key
 * @param defaultValue - Default value if not in storage
 * @param selector - Redux selector to get state value
 * @param action - Redux action creator to dispatch updates
 * @returns [value, setValue] tuple
 *
 * @example
 * const [startDate, setStartDate] = useSessionStorage(
 *   'startDate',
 *   '2024-01-01',
 *   (state) => state.user.dates.start,
 *   (value) => setUserStartDate(value)
 * );
 */
export function useSessionStorage<T>(
  key: string,
  defaultValue: T,
  selector: (state: any) => T,
  action: (value: T) => any
): [T, (value: T | ((prev: T) => T)) => void] {
  const dispatch = useDispatch() as any;
  const reduxValue = useSelector(selector);

  const currentValue = reduxValue ?? defaultValue;

  // Initialize Redux from sessionStorage on mount
  useEffect(() => {
    if (!reduxValue) {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as T;
          dispatch(action(parsed));
        } catch {
          dispatch(action(stored as T));
        }
      } else {
        dispatch(action(defaultValue));
      }
    }
  }, []);

  // Sync state to sessionStorage
  useEffect(() => {
    if (currentValue !== null && currentValue !== undefined) {
      const stringValue =
        typeof currentValue === 'string' ? currentValue : JSON.stringify(currentValue);
      sessionStorage.setItem(key, stringValue);
    }
  }, [key, currentValue]);

  // Setter function
  const setValue = (valueOrUpdater: T | ((prev: T) => T)) => {
    const newValue =
      typeof valueOrUpdater === 'function'
        ? (valueOrUpdater as (prev: T) => T)(currentValue)
        : valueOrUpdater;

    // Prevent unnecessary dispatches by checking if value actually changed
    if (JSON.stringify(newValue) === JSON.stringify(currentValue)) {
      return;
    }

    dispatch(action(newValue));
  };

  return [currentValue, setValue];
}
